/** Mobile camera barcode / QR reading via the native BarcodeDetector API (Chrome / Android / Safari 17+). Falls back gracefully. */
import { useEffect, useRef, useState } from "react";
import { Camera, X } from "lucide-react";

export const cameraSupported = () => typeof window !== "undefined" && !!navigator.mediaDevices?.getUserMedia;
const nativeDetector = () => typeof window !== "undefined" && "BarcodeDetector" in window;
const clean = (v) => { const m = (v || "").match(/\/warehouse\/scan\/([^/?#]+)/); return (m ? decodeURIComponent(m[1]) : v || "").trim(); };

export function CameraScan({ onCode, onClose }) {
  const video = useRef(null); const [error, setError] = useState(null);
  useEffect(() => {
    let stream, timer, stop = false, zx;
    (async () => {
      try {
        if (!nativeDetector()) {  // iOS Safari / Firefox / desktop: ZXing fallback (QR + 1D barcodes)
          const { BrowserMultiFormatReader } = await import("@zxing/browser");
          zx = await new BrowserMultiFormatReader().decodeFromConstraints({ video: { facingMode: "environment" } }, video.current, (res) => { if (res && !stop) { stop = true; zx?.stop(); onCode(clean(res.getText())); } });
          return;
        }
        const det = new window.BarcodeDetector({ formats: ["code_128", "qr_code", "code_39", "ean_13", "data_matrix"] });
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
        video.current.srcObject = stream; await video.current.play();
        const tick = async () => { if (stop) return; try { const codes = await det.detect(video.current); if (codes.length) { let v = codes[0].rawValue || ""; const m = v.match(/\/warehouse\/scan\/([^/?#]+)/); if (m) v = decodeURIComponent(m[1]); onCode(v.trim()); return; } } catch { /* frame not ready */ } timer = setTimeout(tick, 250); };
        tick();
      } catch (e) { setError(e.message || "Camera unavailable"); }
    })();
    return () => { stop = true; clearTimeout(timer); zx?.stop(); stream?.getTracks().forEach((t) => t.stop()); };
  }, [onCode]);
  return (
    <div className="fixed inset-0 z-[60] flex flex-col bg-black" data-testid="camera-scan">
      <div className="flex items-center justify-between p-3 text-white"><span className="inline-flex items-center gap-2 text-[13px] font-semibold"><Camera className="h-4 w-4" />Point the camera at a label</span><button onClick={onClose} className="rounded-full bg-white/20 p-2" data-testid="camera-close"><X className="h-5 w-5" /></button></div>
      {error ? <div className="m-4 rounded-xl bg-rose-600 p-4 text-[13px] text-white" data-testid="camera-error">{error}</div> : <video ref={video} className="flex-1 object-cover" muted playsInline />}
    </div>
  );
}

export function CameraButton({ onCode, className = "", tid = "camera-open" }) {
  const [open, setOpen] = useState(false);
  if (!cameraSupported()) return null;
  return <>
    <button type="button" onClick={() => setOpen(true)} className={`inline-flex items-center justify-center gap-1 rounded-lg border-2 border-slate-300 px-3 text-[11px] font-semibold hover:bg-slate-50 ${className}`} data-testid={tid}><Camera className="h-4 w-4" />Camera</button>
    {open && <CameraScan onCode={(c) => { setOpen(false); onCode(c); }} onClose={() => setOpen(false)} />}
  </>;
}
